/**
 * WHAT A ROUTINE WAITS ON (2026-10-17, Justin: a routine whose app is signed out "shouldn't keep repeating over and
 * over again … the routine should go into pause mode until the user has signed in", and resume by itself when it is).
 *
 * A routine that cannot run until a person fixes something waits instead of failing at every run. It is stored as
 * paused, so every reader that knows only "active" and "paused" keeps it from running, with a `waiting` record that
 * says on what; it resumes by itself once that is fixed. This file is the one closed list both sides read: the lanes a
 * wait can be in, where a person fixes each, and what a client may read of a wait. The record's other fields (who may
 * fix it, the evidence, its clock) are the server's alone (routineWaitCore.ts).
 *
 * Server: functions/src/prest0nSession/routineWaits.ts. Chat: src/types/routineWaits.ts. Byte-identical and importing
 * nothing (tests/prest0nSession/routineWaits.parity.test.ts).
 */

/**
 * The lanes, each named for what clears it:
 *  - 'sign-in': a sign-in in Prest0n's browser (a custom-<slug> connection), cleared when that person signs in again;
 *  - 'connection': a connected app (a toolkit), cleared by a live connection that covers it;
 *  - 'confirmation': a legal app an owner must confirm the firm may use, cleared by that confirmation;
 *  - 'billing': the workspace's billing (its plan, a payment, its credit or a spending limit), cleared when its runs
 *    may go again;
 *  - 'credential': a key the routine holds itself (an API watch's headers), cleared when its endpoint takes it again,
 *    or once a person gives the routine a new one in its thread;
 *  - 'access': someone's access where the routine works — an account its run would send through that someone in its
 *    thread can't see, or a repeating message's author who can no longer post in its thread — cleared once they can.
 */
export const ROUTINE_WAIT_LANES = ['sign-in', 'connection', 'confirmation', 'billing', 'credential', 'access'] as const;
export type RoutineWaitLane = (typeof ROUTINE_WAIT_LANES)[number];

export function isRoutineWaitLane(value: unknown): value is RoutineWaitLane {
  return typeof value === 'string' && (ROUTINE_WAIT_LANES as readonly string[]).includes(value);
}

/** The kind (`metadata.kind`) of the bell row a waiting routine raises. */
export const ROUTINE_WAIT_NOTICE_KIND = 'routine_wait';

/**
 * What an attempt to resume a waiting routine came to (the answer to a person's "Check now"): resumed, or completed
 * (its dates ran out while it waited); stopped (its thread was archived or is gone); gone; not waiting (any more);
 * still blocked, or undetermined (a record could not be read): it keeps waiting; held (it kept failing right after
 * resuming, so only the person starts it).
 */
export const ROUTINE_RESUME_OUTCOMES = ['resumed', 'completed', 'stopped', 'gone', 'not-waiting', 'still-blocked', 'undetermined', 'held'] as const;
export type RoutineResumeOutcome = (typeof ROUTINE_RESUME_OUTCOMES)[number];

export function isRoutineResumeOutcome(value: unknown): value is RoutineResumeOutcome {
  return typeof value === 'string' && (ROUTINE_RESUME_OUTCOMES as readonly string[]).includes(value);
}

/** A browser sign-in's slug, as the Integrations page's `?signin=` link reads it (src/services/composio/signinLink.ts). */
const SIGNIN_SLUG = /^custom-[A-Za-z0-9_-]{1,120}$/;
/** A connected app's toolkit slug. */
const TOOLKIT_SLUG = /^[a-z0-9][a-z0-9_-]{0,63}$/;
/** A thread or a workspace, as a link names it (src/lib/threadDeepLink.ts). */
const ID = /^[A-Za-z0-9_-]{1,128}$/;
/** A wait fixed in a thread names it so: `thread:<threadId>` (a key given to Prest0n there, someone added back). */
const THREAD_TARGET = /^thread:([A-Za-z0-9_-]{1,128})$/;

/** The target of a wait fixed in a thread. */
export function routineWaitThreadTarget(threadId: string): string | null {
  return ID.test(threadId) ? `thread:${threadId}` : null;
}

/**
 * Where a person fixes a wait: the Integrations page, opened on the sign-in (`?signin=`, which opens its window), on
 * the app (`?connect=`, one tap from connecting it, or from sharing it with the workspace) or on the legal apps
 * (`?category=legal`); the subscription page; or the thread the fix is made in (`/?thread=`, in its workspace when
 * that is known). A target that is not its lane's shape opens the Integrations page as it is.
 */
export function routineWaitFixPath(lane: RoutineWaitLane, target: string | null | undefined, accountId?: string | null): string {
  const t = typeof target === 'string' ? target : '';
  if (lane === 'sign-in' && SIGNIN_SLUG.test(t)) return `/integrations?signin=${t}`;
  if ((lane === 'connection' || lane === 'access') && TOOLKIT_SLUG.test(t)) return `/integrations?connect=${t}`;
  if (lane === 'confirmation') return '/integrations?category=legal';
  if (lane === 'billing') return '/subscription';
  const thread = THREAD_TARGET.exec(t);
  if ((lane === 'credential' || lane === 'access') && thread) {
    return typeof accountId === 'string' && ID.test(accountId) ? `/?thread=${thread[1]}&ws=${accountId}` : `/?thread=${thread[1]}`;
  }
  return '/integrations';
}

/** A fix link is only ever one of those shapes: a link read back from a stored row is checked before it is followed. */
export function isRoutineWaitFixPath(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value === '/integrations' || value === '/integrations?category=legal' || value === '/subscription') return true;
  if (/^\/\?thread=[A-Za-z0-9_-]{1,128}(?:&ws=[A-Za-z0-9_-]{1,128})?$/.test(value)) return true;
  const m = /^\/integrations\?(signin|connect)=([^&#]+)$/.exec(value);
  if (!m) return false;
  return m[1] === 'signin' ? SIGNIN_SLUG.test(m[2]) : TOOLKIT_SLUG.test(m[2]);
}

/**
 * What a client may read of a wait: the lane, the app as people name it, since when, where to fix it, and whether a
 * fix resumes the routine by itself (false once it kept failing right after resuming; then only the person's Resume
 * or Check now does).
 */
export interface RoutineWaitView {
  lane: RoutineWaitLane;
  app: string;
  sinceMs: number;
  fixPath: string;
  autoResume: boolean;
}

/**
 * A wait read defensively, from the stored record (which names its `target`; its workspace is the routine's) or from a
 * row the server sent (which carries its `fixPath`): anything malformed is no wait, never a guess.
 */
export function readRoutineWaitView(raw: unknown, accountId?: string | null): RoutineWaitView | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (!isRoutineWaitLane(r.lane)) return null;
  const app = typeof r.app === 'string' ? r.app.trim() : '';
  if (!app || app.length > 80) return null;
  if (typeof r.sinceMs !== 'number' || !Number.isFinite(r.sinceMs) || r.sinceMs <= 0) return null;
  const fixPath = isRoutineWaitFixPath(r.fixPath)
    ? r.fixPath
    : routineWaitFixPath(r.lane, typeof r.target === 'string' ? r.target : null, accountId);
  return { lane: r.lane, app, sinceMs: r.sinceMs, fixPath, autoResume: r.autoResume !== false };
}
